import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import axios from 'axios';
import PostCard from '../components/feed/PostCard';
import { StoriesBar, CreateStoryModal } from '../components/feed/Stories';
import { Loader2, Users, TrendingUp, Home, RefreshCw, Sparkles, Flame, Clock, Calendar, ChevronRight, DollarSign, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { cn, getInitials } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Featured Services Component
const FeaturedServices = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setServices(response.data.services || []);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [token]);

  if (loading || services.length === 0) return null;

  return (
    <div className="py-4 border-b border-border/50">
      <div className="flex items-center justify-between px-4 mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-pink-500" />
          <h2 className="font-semibold">Available Services</h2>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-pink-500 rounded-full"
          onClick={() => navigate('/appointments')}
        >
          See All
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      
      <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
        {services.map(service => (
          <div 
            key={service.id}
            onClick={() => navigate(`/profile/${service.provider_username}`)}
            className="flex-shrink-0 w-48 bg-card border rounded-xl p-3 cursor-pointer hover:shadow-lg hover:border-pink-500/50 transition-all"
            data-testid={`featured-service-${service.id}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Avatar className="w-8 h-8">
                <AvatarImage src={service.provider_avatar} />
                <AvatarFallback className="bg-gradient-to-br from-pink-500/20 to-violet-500/20 text-xs">
                  {getInitials(service.provider_display_name || service.provider_username)}
                </AvatarFallback>
              </Avatar>
              <p className="text-xs text-muted-foreground truncate">@{service.provider_username}</p>
            </div>
            <h3 className="font-medium text-sm truncate mb-1">{service.name}</h3>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{service.duration_minutes} min</span>
              <span className={cn(
                "font-bold text-sm",
                service.is_free ? "text-green-500" : "text-pink-500"
              )}>
                {service.is_free ? 'Gratis' : `$${service.price}`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function HomePage() {
  const { token, language, user } = useAuth();
  const tr = t(language);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [activeTab, setActiveTab] = useState('home'); // 'home', 'trending', 'following'
  const [sortType, setSortType] = useState('recent');

  // Tab configuration
  const tabs = [
    { id: 'home', label: 'Home', icon: Home, description: 'All posts' },
    { id: 'foryou', label: 'For You', icon: Sparkles, description: 'Personalized recommendations' },
    { id: 'trending', label: 'Trending', icon: Flame, description: 'Popular content' },
    { id: 'following', label: 'Following', icon: Users, description: 'From people you follow' },
  ];

  const fetchPosts = useCallback(async (skip = 0, isRefresh = false) => {
    try {
      let endpoint;
      let headers = { Authorization: `Bearer ${token}` };
      
      switch (activeTab) {
        case 'following':
          endpoint = `${API}/posts/feed?skip=${skip}&limit=20`;
          break;
        case 'foryou':
          endpoint = `${API}/feed/mixed?skip=${skip}&limit=20`;
          break;
        case 'trending':
          endpoint = `${API}/public/posts?skip=${skip}&limit=20&sort=popular`;
          break;
        case 'home':
        default:
          endpoint = `${API}/public/posts?skip=${skip}&limit=20&sort=${sortType}`;
          break;
      }
      
      const response = await axios.get(endpoint, { headers });
      
      if (skip === 0 || isRefresh) {
        setPosts(response.data);
      } else {
        setPosts(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.data.length === 20);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    }
  }, [token, activeTab, sortType]);

  // Fetch posts when tab changes
  useEffect(() => {
    setLoading(true);
    setPosts([]);
    fetchPosts();
  }, [activeTab, sortType]);

  // Refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPosts(0, true);
  };

  // === Auto-refresh feed in the background ===
  // Every 30s while the tab is visible, silently re-fetch the first page.
  // - If new posts exist AND user is scrolled to the top → merge transparently.
  // - If user is scrolled down → show a "X new posts" pill they can tap.
  // - Always update like/comment/views counters on already-displayed posts.
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [pendingPosts, setPendingPosts] = useState([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const pollOnce = async () => {
      if (document.hidden) return;
      try {
        let endpoint;
        switch (activeTab) {
          case 'following':
            endpoint = `${API}/posts/feed?skip=0&limit=20`; break;
          case 'foryou':
            endpoint = `${API}/feed/mixed?skip=0&limit=20`; break;
          case 'trending':
            endpoint = `${API}/public/posts?skip=0&limit=20&sort=popular`; break;
          default:
            endpoint = `${API}/public/posts?skip=0&limit=20&sort=${sortType}`;
        }
        const r = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        const fresh = r.data || [];
        setPosts((current) => {
          if (current.length === 0) return fresh;
          const knownIds = new Set(current.map((p) => p.id));
          const brandNew = fresh.filter((p) => !knownIds.has(p.id));
          // Update counters on existing posts
          const updated = current.map((p) => {
            const match = fresh.find((f) => f.id === p.id);
            if (!match) return p;
            return {
              ...p,
              likes_count: match.likes_count ?? p.likes_count,
              comments_count: match.comments_count ?? p.comments_count,
              views_count: match.views_count ?? p.views_count,
            };
          });
          // If at top of page → merge brand-new posts transparently
          if (brandNew.length && window.scrollY < 200) {
            return [...brandNew, ...updated];
          }
          if (brandNew.length) {
            setPendingPosts(brandNew);
            setNewPostsCount(brandNew.length);
          }
          return updated;
        });
      } catch {}
    };

    const interval = setInterval(pollOnce, 30000);
    const onVisibility = () => { if (!document.hidden) pollOnce(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, activeTab, sortType]);

  const applyPendingPosts = useCallback(() => {
    if (!pendingPosts.length) return;
    setPosts((current) => [...pendingPosts, ...current]);
    setPendingPosts([]);
    setNewPostsCount(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pendingPosts]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    const scrollTop = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    
    if (scrollTop + clientHeight >= scrollHeight - 500) {
      setLoadingMore(true);
      fetchPosts(posts.length);
    }
  }, [loadingMore, hasMore, posts.length, fetchPosts]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handlePostUpdate = (postId, updates) => {
    setPosts(prev => prev.map(post => 
      post.id === postId ? { ...post, ...updates } : post
    ));
  };

  const handlePostDelete = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
  };

  return (
    <div className="max-w-2xl mx-auto pb-20" data-testid="home-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        {/* Title Row */}
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
              Feed
            </h1>
            <p className="text-xs text-muted-foreground">
              {activeTab === 'home' && 'Discover new content'}
              {activeTab === 'foryou' && 'Personalized for you'}
              {activeTab === 'trending' && 'Popular right now'}
              {activeTab === 'following' && 'From people you follow'}
            </p>
          </div>
          
          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-feed-btn"
          >
            <RefreshCw className={cn("w-5 h-5", refreshing && "animate-spin")} />
          </Button>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-1 px-4 pb-3">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              size="sm"
              className={cn(
                "rounded-full flex-1 transition-all",
                activeTab === tab.id 
                  ? "bg-gradient-to-r from-pink-500 to-violet-500 text-white shadow-lg shadow-pink-500/20" 
                  : "hover:bg-secondary"
              )}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-1.5" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Sort Options for Home tab */}
        {activeTab === 'home' && (
          <div className="flex gap-2 px-4 pb-3">
            <Button
              variant={sortType === 'recent' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-full text-xs h-7"
              onClick={() => setSortType('recent')}
            >
              <Clock className="w-3 h-3 mr-1" />
              Recent
            </Button>
            <Button
              variant={sortType === 'popular' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-full text-xs h-7"
              onClick={() => setSortType('popular')}
            >
              <Sparkles className="w-3 h-3 mr-1" />
              Popular
            </Button>
          </div>
        )}
      </header>
      
      {/* Stories Bar */}
      <StoriesBar onCreateStory={() => setShowCreateStory(true)} />
      
      {/* Featured Services */}
      <FeaturedServices />
      
      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-pink-500 mb-4" />
          <p className="text-muted-foreground">Loading posts...</p>
        </div>
      ) : (
        <>
          {/* Feed */}
          <div className="divide-y divide-border/40">
            {posts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4">
                {activeTab === 'following' ? (
                  <>
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500/20 to-violet-500/20 flex items-center justify-center mb-4">
                      <Users className="w-10 h-10 text-pink-500" />
                    </div>
                    <p className="text-lg font-medium text-center mb-2">No posts yet</p>
                    <p className="text-muted-foreground text-center mb-6 max-w-sm">
                      Follow some creators to see their posts here
                    </p>
                    <Button 
                      onClick={() => setActiveTab('trending')} 
                      className="rounded-full bg-gradient-to-r from-pink-500 to-violet-500"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Discover Trending
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500/20 to-violet-500/20 flex items-center justify-center mb-4">
                      <Sparkles className="w-10 h-10 text-pink-500" />
                    </div>
                    <p className="text-lg font-medium text-center mb-2">No posts yet</p>
                    <p className="text-muted-foreground text-center">Be the first to post!</p>
                  </>
                )}
              </div>
            ) : (
              posts.map((post, index) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onUpdate={handlePostUpdate}
                  onDelete={handlePostDelete}
                  style={{ animationDelay: `${index * 30}ms` }}
                  className="animate-fade-in"
                />
              ))
            )}
          </div>
          
          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex justify-center py-8">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
                <span className="text-sm text-muted-foreground">Loading more...</span>
              </div>
            </div>
          )}
          
          {/* End of feed */}
          {!hasMore && posts.length > 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">You're all caught up!</p>
              <Button 
                variant="link" 
                className="text-pink-500 mt-2"
                onClick={handleRefresh}
              >
                Refresh feed
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create Story Modal */}
      <CreateStoryModal 
        open={showCreateStory} 
        onClose={() => setShowCreateStory(false)} 
      />

      {/* New-posts pill — appears when background polling finds new posts
          and the user is scrolled away from the top. */}
      {newPostsCount > 0 && (
        <button
          type="button"
          onClick={applyPendingPosts}
          data-testid="new-posts-pill"
          className="fixed left-1/2 -translate-x-1/2 top-20 z-30
                     bg-primary text-primary-foreground rounded-full px-4 py-2
                     shadow-lg ring-2 ring-primary/40
                     animate-bounce-in font-semibold flex items-center gap-2"
        >
          ↑ {newPostsCount} new post{newPostsCount > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}
